import { useSortable } from '@dnd-kit/sortable';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Else, If, Then, When } from 'react-if';
import { Button } from '../../../../common/view/button/button/button';
import { Color } from '../../../../common/view/color/color';
import { EditableText } from '../../../../common/view/editable-text/editable-text';
import { EffectContext } from '../../../../common/view/effect/effect.context';
import { MonoIcon } from '../../../../common/view/icon/mono/mono-icon';
import { Menu } from '../../../../common/view/menu/menu';
import { MenuItem } from '../../../../common/view/menu/menu-item';
import { MenuPositions } from '../../../../common/view/menu/menu-positions';
import { DndKit } from '../../../../external/dnd-kit/dnd-kit';
import { ImmerFuncs } from '../../../../external/immer/immer.funcs';
import { NodePurposeWire } from '../../../../external/server/node-purpose.wire';
import { ServerApi } from '../../../../external/server/server.api';
import { CssFuncs } from '../../../../foundation/css/css.funcs';
import { RectFuncs } from '../../../../foundation/geometry/rect.funcs';
import { ArrayFuncs } from '../../../../foundation/js/array.funcs';
import { StringFuncs } from '../../../../foundation/js/string.funcs';
import { GapY } from '../../../../foundation/layout/gap-y';
import { StackY } from '../../../../foundation/layout/stack-y';
import { TripleX } from '../../../../foundation/layout/triple-x';
import { MegaBoardColWire, MegaBoardContentWire } from '../../../core/board/mega/mega-board-content.wire';
import { MegaBoardContentWireFuncs } from '../../../core/board/mega/mega-board-content.wire.funcs';
import { NodeStore } from '../../../core/node/node.store';
import { NodeWireFuncs } from '../../../core/node/node.wire.funcs';
import { AppStore } from '../../app/app.store';
import { NodeItem } from '../../node/node-item';
import { MegaBoardColUpgradeButton } from './mega-board-col-upgrade-button';
import { MegaBoardColStyle } from './mega-board-col.style';
import { MegaBoardItem } from './mega-board-item';

export function MegaBoardCol({ rowId, col }: { rowId: string; col?: MegaBoardColWire }) {
  const [hasText, setHasText] = useState(StringFuncs.isNotEmpty(col?.text));
  const [focused, setFocused] = useState(false);
  const sortable = useSortable({ id: col?.id ?? '', data: { type: 'mega-board-col', text: col?.text } });
  const { popupApi } = useContext(EffectContext);
  const moreButtonRef = useRef<HTMLDivElement>(null);

  const className = useMemo(() => {
    return CssFuncs.className((names) => {
      names.push(col ? 'is-not-placeholder' : 'is-placeholder');
      names.push(focused ? 'is-focused' : 'is-not-focused');
    });
  }, [col, focused]);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(
    async (text: string) => {
      setFocused(false);
      if (col) {
        // update existing col
        const currentBoardId = AppStore.get().currentBoardId!;
        const board = NodeStore.getItem(currentBoardId);
        const sub = ImmerFuncs.produce(board.sub as MegaBoardContentWire, (draft) => {
          MegaBoardContentWireFuncs.updateCol(draft, { rowId: rowId, colId: col.id, col: { text } });
        });
        NodeStore.updateItem(currentBoardId, { sub });
        ServerApi.updateNode(currentBoardId, { sub });
        //
      } else {
        // add new col
        if (StringFuncs.isEmpty(text)) {
          return;
        }

        const virtualNode = NodeWireFuncs.init({
          purpose: NodePurposeWire.virtual,
        });
        const createdVirtualNode = await ServerApi.createNode(virtualNode);
        NodeStore.updateItem(createdVirtualNode.id, createdVirtualNode);

        const currentBoardId = AppStore.get().currentBoardId!;
        const board = NodeStore.getItem(currentBoardId);
        const sub = ImmerFuncs.produce(board.sub as MegaBoardContentWire, (draft) => {
          MegaBoardContentWireFuncs.addCol(draft, { rowId: rowId, col: { text, virtualNodeId: createdVirtualNode.id } });
        });
        NodeStore.updateItem(currentBoardId, { sub });
        ServerApi.updateNode(currentBoardId, { sub });
      }
    },
    [col, rowId],
  );

  const handleChange = useCallback((text: string) => {
    setHasText(StringFuncs.isNotEmpty(text));
  }, []);

  const handleRemove = useCallback(() => {
    const currentBoardId = AppStore.get().currentBoardId!;
    const board = NodeStore.getItem(currentBoardId);
    const sub = ImmerFuncs.produce(board.sub as MegaBoardContentWire, (draft) => {
      MegaBoardContentWireFuncs.removeCol(draft, { rowId: rowId, colId: col?.id! });
    });
    NodeStore.updateItem(currentBoardId, { sub });
    ServerApi.updateNode(currentBoardId, { sub });
  }, [col, rowId]);

  const isPlaceholder = !col;

  const needUpgrade = col && !col.virtualNodeId;

  const handleUpgrade = useCallback(async () => {
    if (!col) {
      return;
    }

    const virtualNode = NodeWireFuncs.init({
      purpose: NodePurposeWire.virtual,
      sub: col.items.map((i) => i.id),
    });
    const createdVirtualNode = await ServerApi.createNode(virtualNode);
    NodeStore.updateItem(createdVirtualNode.id, createdVirtualNode);

    const currentBoardId = AppStore.get().currentBoardId!;
    const board = NodeStore.getItem(currentBoardId);
    const sub = ImmerFuncs.produce(board.sub as MegaBoardContentWire, (draft) => {
      MegaBoardContentWireFuncs.updateCol(draft, { rowId: rowId, colId: col.id, col: { virtualNodeId: createdVirtualNode.id, items: [] } });
    });
    NodeStore.updateItem(currentBoardId, { sub });
    ServerApi.updateNode(currentBoardId, { sub });
  }, [col, rowId]);

  return (
    <MegaBoardColStyle.Container
      className={className}
      ref={col && sortable.setNodeRef}
      {...(col && sortable.attributes)}
      style={col && { transform: DndKit.CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
    >
      <MegaBoardColStyle.Anchor>
        <MegaBoardColStyle.TopButton>
          <Button icon={MonoIcon.gripHorizontal} color={Color.white7} {...sortable.listeners} />
        </MegaBoardColStyle.TopButton>
      </MegaBoardColStyle.Anchor>
      <TripleX.Container $align="center" $gap="10px">
        <TripleX.Rest>
          <MegaBoardColStyle.TitleContainer>
            <MegaBoardColStyle.TitleText>
              <EditableText text={col?.text} onFocus={handleFocus} onBlur={handleBlur} onChange={handleChange} />
            </MegaBoardColStyle.TitleText>
            <When condition={isPlaceholder && !hasText}>
              <MegaBoardColStyle.Placeholder>{'New Column'}</MegaBoardColStyle.Placeholder>
            </When>
          </MegaBoardColStyle.TitleContainer>
        </TripleX.Rest>
        <TripleX.Side>
          <MegaBoardColStyle.ActionButton>
            <Button
              theRef={moreButtonRef}
              icon={MonoIcon.more}
              color={Color.white7}
              onClick={() => {
                const triggerRect = RectFuncs.from(moreButtonRef.current?.getBoundingClientRect() ?? {});
                popupApi.push({
                  component: (
                    <Menu>
                      <MenuItem icon={MonoIcon.trash} text={'Remove Column'} danger onClick={handleRemove} />
                    </Menu>
                  ),
                  triggerRect,
                  positions: MenuPositions,
                });
              }}
            />
          </MegaBoardColStyle.ActionButton>
        </TripleX.Side>
        <When condition={needUpgrade}>
          <TripleX.Side>
            <MegaBoardColUpgradeButton text={'Upgrade'} onClick={handleUpgrade} />
          </TripleX.Side>
        </When>
      </TripleX.Container>
      <GapY $gap="10px" />
      <When condition={!isPlaceholder}>
        <If condition={col?.virtualNodeId}>
          {/* render for col v2 */}
          <Then>
            <NodeItem nodeId={col?.virtualNodeId} displaySubItemsOnly displayNewSubItemPlaceholder />
          </Then>

          {/* render for col v1 */}
          <Else>
            <StackY $gap="2px">
              {col?.items.map((item, i) => (
                <MegaBoardItem key={item.id} rowId={rowId} colId={col.id} item={item} hasPrev={i > 0} hasNext={i < ArrayFuncs.ensure(col?.items).length - 1} />
              ))}
              <MegaBoardItem key={col?.items.length} rowId={rowId} colId={col?.id ?? ''} />
            </StackY>
          </Else>
        </If>
      </When>
    </MegaBoardColStyle.Container>
  );
}
